import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import { inr } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function EmiChart({ loan, onPay, paying }) {
  if (!loan || loan.loan_taken <= 0) {
    return (
      <Card data-testid="emi-chart" className="rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg font-display">Loan EMI Chart</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground py-6 text-center">
            Aapka koi active loan nahi hai. 🎉
          </p>
        </CardContent>
      </Card>
    );
  }

  const data = loan.schedule.map((s) => ({
    name: `M${s.month}`,
    Principal: s.principal,
    Interest: s.interest,
  }));

  const nextDue = loan.schedule.find((s) => !s.paid);

  return (
    <Card data-testid="emi-chart" className="rounded-xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg font-display">Loan EMI Chart</CardTitle>
        {loan.is_npa && <Badge className="bg-red-500 text-white">NPA</Badge>}
      </CardHeader>
      <CardContent>
        {nextDue && (
          <div
            data-testid="next-emi-bar"
            className="mb-5 flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4"
          >
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Agli EMI · {nextDue.due_date}
              </p>
              <p className="text-xl font-mono font-bold" data-testid="next-emi-amount">
                {inr(nextDue.emi)}
              </p>
            </div>
            <Button data-testid="pay-emi-btn" onClick={onPay} disabled={paying}>
              {paying ? "Processing…" : "Pay EMI"}
            </Button>
          </div>
        )}
        <div className="h-56 w-full" style={{ minHeight: 224 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                formatter={(v) => inr(v)}
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Principal" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Interest" stackId="a" fill="hsl(var(--accent))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-2">Due Date</th>
                <th className="py-2 pr-2 text-right">EMI</th>
                <th className="py-2 pr-2 text-right">Baaki</th>
                <th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {loan.schedule.slice(0, 6).map((s) => (
                <tr key={s.month} className="border-b border-border/60">
                  <td className="py-2 pr-2 font-sans">{s.due_date}</td>
                  <td className="py-2 pr-2 text-right">{inr(s.emi)}</td>
                  <td className="py-2 pr-2 text-right text-muted-foreground">{inr(s.balance)}</td>
                  <td className="py-2 text-right">
                    <span className={s.paid ? "text-emerald-600" : "text-amber-600"}>
                      {s.paid ? "Paid" : "Due"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
